import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import useSessionManager from '../../hooks/useSessionManager';
import { fetchData } from '../../apiService';
import {
  TrendingUp,
  Receipt,
  Payment,
  Inventory,
  ShoppingCart,
  Assessment,
  Warning,
  Schedule,
  LocalShipping,
  CheckCircle,
  Error,
  Info,
  Refresh,
  BarChart,
  Assignment,
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@mui/icons-material';
import './Home.css';
import EnquiryModal from './Enquiry/EnquiryModal';
import OrbitalLoader from '../common/OrbitalLoader';

const Home = () => {
  const { validateSession } = useSessionManager();
  const { token, user, companyId, branchId } = useAuth();
  const history = useHistory();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    todayRevenue: 0,
    monthRevenue: 0,
    activeRentals: 0,
    availableEquipment: 0,
    pendingReturns: 0,
    overdueReturns: 0,
    totalClients: 0,
    totalVendors: 0,
    receivables: 0,
    payables: 0,
    cashBalance: 0,
    revenueGrowth: 0,
    equipmentUtilization: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Branch Wise Revenue State
  const [branchRevenueData, setBranchRevenueData] = useState([]);
  const [loadingBranchRevenue, setLoadingBranchRevenue] = useState(false);
  
  // Product Enquiry Modal State
  const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  
  // Date Report Modal State
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedDateData, setSelectedDateData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState({});

  // Date range for delivery/estimate/enquiry APIs
  const [fromDate] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  });
  const [toDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString('en-IN');
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getEventsForDate = (date) => {
    return calendarEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === date.getDate() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getFullYear() === date.getFullYear();
    });
  };

  const changeMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const handleDateClick = async (date) => {
    if (!companyId || !token) return;
    
    setLoadingReport(true);
    setReportModalOpen(true);
    setExpandedCustomers({});
    
    try {
      // Format date as YYYY-MM-DD without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      console.log('Clicked date:', date, 'Formatted:', formattedDate);
      
      // Fetch data from ReportApi
      const response = await fetchData(
        'ReportApi',
        `/ForecastDatewise/${formattedDate}/${companyId}/${companyId}`,
        token
      );
      
      setSelectedDateData({
        date: formattedDate,
        data: response.response_data || response || []
      });
    } catch (error) {
      console.error('Error fetching date report:', error);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      setSelectedDateData({
        date: formattedDate,
        data: [],
        error: 'Failed to load report data'
      });
    } finally {
      setLoadingReport(false);
    }
  };

  const toggleCustomer = (customerKey) => {
    setExpandedCustomers(prev => ({
      ...prev,
      [customerKey]: !prev[customerKey]
    }));
  };

  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Group data by customer
  const groupDataByCustomer = (data) => {
    if (!data || data.length === 0) return [];
    
    const grouped = data.reduce((acc, item) => {
      const customerKey = `${item.CustomerName}_${item.ContactNumber1}_${item.AddressLine1}`;
      if (!acc[customerKey]) {
        acc[customerKey] = {
          customerName: item.CustomerName,
          contactNumber: item.ContactNumber1,
          addressLine1: item.AddressLine1,
          addressLine2: item.AddressLine2,
          items: []
        };
      }
      acc[customerKey].items.push(item);
      return acc;
    }, {});
    
    return Object.values(grouped);
  };

  const fetchBranchRevenue = useCallback(async () => {
    if (!token || !companyId) return;
    
    setLoadingBranchRevenue(true);
    try {
      // API parameters: tmpType, toDate, companyId, itemId
      // Using 'all' for tmpType, current date for toDate, and 0 for itemId (all items)
      const tmpType = 'RevenueReport';
      const itemId = 0;
      
      const response = await fetchData(
        'ReportApi',
        `/${tmpType}/${toDate}/${companyId}/${itemId}`,
        token
      );
      
      setBranchRevenueData(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching branch revenue:', error);
      setBranchRevenueData([]);
    } finally {
      setLoadingBranchRevenue(false);
    }
  }, [token, companyId, toDate]);

  const fetchDashboardData = useCallback(async () => {
    try {
      
      // If no token or companyId, just show empty dashboard
      if (!token || !companyId) {
        console.log('No token or companyId, showing empty dashboard');
        console.log('Token:', token ? 'exists' : 'missing');
        console.log('CompanyId:', companyId);
        console.log('User:', user);
        setLoading(false);
        return;
      }

      console.log('Fetching dashboard data for company:', companyId);
      
      // Fetch branch revenue data
      fetchBranchRevenue();
      
      // Fetch multiple endpoints in parallel
      const [
        salesData,
        purchaseData,
        clientsData,
        vendorsData,
        itemsData,
        receiptsData,
        deliveryOutData,
        deliveryInData,
        estimateData,
        enquiryData,
        customerData,
        itemStockData
      ] = await Promise.allSettled([
        fetchData('SaleApi', `/SalesList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(e => { console.log('Sales fetch error:', e); return { response_data: [] }; }),
        fetchData('PurchaseApi', `/PurchaseList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(e => { console.log('Purchase fetch error:', e); return { response_data: [] }; }),
        fetchData('ClientApi', `/${companyId}`, token).catch(e => { console.log('Client fetch error:', e); return { response_data: [] }; }),
        fetchData('VendorApi', `/${companyId}`, token).catch(e => { console.log('Vendor fetch error:', e); return { response_data: [] }; }),
        fetchData('ItemApi', `/${companyId}`, token).catch(e => { console.log('Item fetch error:', e); return { response_data: [] }; }),
        fetchData('PaymentReceiptsApi', `/PaymentReceiptsList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(e => { console.log('Receipts fetch error:', e); return { response_data: [] }; }),
        fetchData('DeliveryCallApi', `/DeliveryOutList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(e => { console.log('DeliveryOut fetch error:', e); return { response_data: [] }; }),
        fetchData('DeliveryCallApi', `/DeliveryInList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(e => { console.log('DeliveryIn fetch error:', e); return { response_data: [] }; }),
        fetchData('EstimateApi', `/EstimateList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(e => { console.log('Estimate fetch error:', e); return { response_data: [] }; }),
        fetchData('EnquiryApi', `/EnquriyList/${companyId}/${branchId || 0}/${fromDate}/${toDate}`, token).catch(e => { console.log('Enquiry fetch error:', e); return { response_data: [] }; }),
        fetchData('CustomerApi', `/${companyId}`, token).catch(e => { console.log('Customer fetch error:', e); return { response_data: [] }; }),
        fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId || 0}`, token).catch(e => { console.log('ItemStock fetch error:', e); return { response_data: [] }; })
      ]);

      console.log('Data fetched successfully');

      // Process all data - handle both array and response_data structure
      const salesResponse = salesData.status === 'fulfilled' ? salesData.value : { response_data: [] };
      const purchasesResponse = purchaseData.status === 'fulfilled' ? purchaseData.value : { response_data: [] };
      const clientsResponse = clientsData.status === 'fulfilled' ? clientsData.value : { response_data: [] };
      const vendorsResponse = vendorsData.status === 'fulfilled' ? vendorsData.value : { response_data: [] };
      const itemsResponse = itemsData.status === 'fulfilled' ? itemsData.value : { response_data: [] };
      const receiptsResponse = receiptsData.status === 'fulfilled' ? receiptsData.value : { response_data: [] };
      
      const sales = salesResponse.response_data || salesResponse || [];
      const purchases = purchasesResponse.response_data || purchasesResponse || [];
      const clients = clientsResponse.response_data || clientsResponse || [];
      const vendors = vendorsResponse.response_data || vendorsResponse || [];
      const items = itemsResponse.response_data || itemsResponse || [];
      const receipts = receiptsResponse.response_data || receiptsResponse || [];
      
      // Handle API responses with response_data structure
      const deliveryOutResponse = deliveryOutData.status === 'fulfilled' ? deliveryOutData.value : { response_data: [] };
      const deliveryInResponse = deliveryInData.status === 'fulfilled' ? deliveryInData.value : { response_data: [] };
      const estimateResponse = estimateData.status === 'fulfilled' ? estimateData.value : { response_data: [] };
      const enquiryResponse = enquiryData.status === 'fulfilled' ? enquiryData.value : { response_data: [] };
      
      const deliveryOut = deliveryOutResponse.response_data || [];
      const deliveryIn = deliveryInResponse.response_data || [];
      const estimates = estimateResponse.response_data || [];
      const enquiries = enquiryResponse.response_data || [];
      
      // Handle customer and item stock data for modal
      const customerResponse = customerData.status === 'fulfilled' ? customerData.value : { response_data: [] };
      const itemStockResponse = itemStockData.status === 'fulfilled' ? itemStockData.value : { response_data: [] };
      const customersData = customerResponse.response_data || [];
      const itemsStockData = itemStockResponse.response_data || [];
      
      setCustomers(customersData);
      setItems(itemsStockData);

      console.log('Processed data:', { 
        sales: sales.length, 
        purchases: purchases.length, 
        clients: clients.length, 
        vendors: vendors.length, 
        items: items.length, 
        receipts: receipts.length,
        deliveryOut: deliveryOut.length,
        deliveryIn: deliveryIn.length,
        estimates: estimates.length,
        enquiries: enquiries.length
      });

      // Debug: Log sample data to check structure
      console.log('Sample delivery out:', deliveryOut[0]);
      console.log('Sample estimate:', estimates[0]);
      console.log('Sample enquiry:', enquiries[0]);
      console.log('Sample sale:', sales[0]);
      console.log('Sales response structure:', salesResponse);
      console.log('Sales array:', sales);
      console.log('Sales count:', sales.length);

      // Calculate today's revenue
      const today = new Date().toDateString();
      const todayRevenue = sales
        .filter(s => new Date(s.SaleDate || s.saleDate || s.BillDate).toDateString() === today)
        .reduce((sum, s) => sum + (s.TotalAmount || s.totalAmount || 0), 0);

      // Calculate month revenue
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const monthRevenue = sales
        .filter(s => {
          const date = new Date(s.SaleDate || s.saleDate || s.BillDate);
          return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
        })
        .reduce((sum, s) => sum + (s.TotalAmount || s.totalAmount || 0), 0);

      // Calculate total sales
      const totalSales = sales.reduce((sum, s) => sum + (s.TotalAmount || s.totalAmount || 0), 0);

      // Calculate receivables and payables
      const receivables = sales
        .filter(s => s.PaymentStatus !== 'Paid' && s.paymentStatus !== 'Paid')
        .reduce((sum, s) => sum + ((s.TotalAmount || s.totalAmount || 0) - (s.PaidAmount || s.paidAmount || 0)), 0);

      const payables = purchases
        .filter(p => p.PaymentStatus !== 'Paid' && p.paymentStatus !== 'Paid')
        .reduce((sum, p) => sum + ((p.TotalAmount || p.totalAmount || 0) - (p.PaidAmount || p.paidAmount || 0)), 0);

      // Calculate cash balance from receipts
      const totalReceipts = receipts.reduce((sum, r) => sum + (r.Amount || r.amount || 0), 0);
      const totalPayments = purchases.reduce((sum, p) => sum + (p.PaidAmount || p.paidAmount || 0), 0);
      const cashBalance = totalReceipts - totalPayments;

      // Count delivery out by status
      const deliveryOutPending = deliveryOut.filter(d => d.Status === 'Pending' || !d.Status).length;
      const deliveryOutCompleted = deliveryOut.filter(d => d.Status === 'Completed').length;
      
      // Count delivery in by status
      const deliveryInPending = deliveryIn.filter(d => d.Status === 'Pending' || !d.Status).length;
      const deliveryInCompleted = deliveryIn.filter(d => d.Status === 'Completed').length;

      // Count estimates by status
      const estimatesPending = estimates.filter(e => e.Status === 'Pending' || !e.Status).length;
      const estimatesApproved = estimates.filter(e => e.Status === 'Approved').length;
      const estimatesRejected = estimates.filter(e => e.Status === 'Rejected').length;

      // Count enquiries by status
      const enquiriesPending = enquiries.filter(e => e.Status === 'Pending' || !e.Status).length;
      const enquiriesConverted = enquiries.filter(e => e.Status === 'Converted').length;

      // Get recent activities
      const recentSales = sales.slice(0, 5).map(s => ({
        type: 'sale',
        title: `Sale to ${s.CustomerName || s.clientName || 'Client'}`,
        amount: s.TotalAmount || s.totalAmount || 0,
        date: new Date(s.SaleDate || s.saleDate || s.BillDate).toLocaleDateString(),
        id: s.Id || s.id
      }));

      const recentDeliveries = deliveryOut.slice(0, 3).map(d => ({
        type: 'delivery',
        title: `Delivery Out - ${d.CustomerName || 'Client'}`,
        amount: d.TotalAmount || 0,
        date: new Date(d.DeliveryDate || d.BillDate || d.CreatedDate).toLocaleDateString(),
        id: d.Id
      }));

      const recentEstimates = estimates.slice(0, 3).map(e => ({
        type: 'estimate',
        title: `Estimate - ${e.CustomerName || 'Client'}`,
        amount: e.TotalAmount || 0,
        date: new Date(e.EstimateDate || e.BillDate || e.CreatedDate).toLocaleDateString(),
        id: e.Id
      }));

      const allActivities = [...recentSales, ...recentDeliveries, ...recentEstimates]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 10);

      // Generate alerts
      const newAlerts = [];
      
      // Overdue receivables
      const overdueReceivables = sales.filter(s => {
        if (s.PaymentStatus === 'Paid' || s.paymentStatus === 'Paid') return false;
        const dueDate = new Date(s.DueDate || s.dueDate);
        return dueDate < new Date();
      });
      
      if (overdueReceivables.length > 0) {
        newAlerts.push({
          type: 'overdue',
          title: `${overdueReceivables.length} Overdue Receivables`,
          amount: overdueReceivables.reduce((sum, s) => sum + ((s.TotalAmount || s.totalAmount || 0) - (s.PaidAmount || s.paidAmount || 0)), 0),
          note: 'Immediate attention required'
        });
      }

      // Pending deliveries
      if (deliveryOutPending > 0) {
        newAlerts.push({
          type: 'pending',
          title: `${deliveryOutPending} Pending Deliveries`,
          note: 'Awaiting delivery'
        });
      }

      // Pending estimates
      if (estimatesPending > 0) {
        newAlerts.push({
          type: 'pending',
          title: `${estimatesPending} Pending Estimates`,
          note: 'Awaiting approval'
        });
      }

      // Low stock items
      const lowStock = items.filter(i => (i.CurrentStock || i.currentStock || 0) < (i.MinStock || i.minStock || 10));
      if (lowStock.length > 0) {
        newAlerts.push({
          type: 'stock',
          title: `${lowStock.length} Items Low on Stock`,
          note: 'Reorder required'
        });
      }

      setDashboardData({
        todayRevenue,
        monthRevenue,
        totalSales,
        deliveryOutTotal: deliveryOut.length,
        deliveryOutPending,
        deliveryOutCompleted,
        deliveryInTotal: deliveryIn.length,
        deliveryInPending,
        deliveryInCompleted,
        estimatesTotal: estimates.length,
        estimatesPending,
        estimatesApproved,
        estimatesRejected,
        enquiriesTotal: enquiries.length,
        enquiriesPending,
        enquiriesConverted,
        totalClients: clients.length,
        totalVendors: vendors.length,
        receivables,
        payables,
        cashBalance,
        totalReceipts,
        totalPayments,
        availableEquipment: items.filter(i => (i.CurrentStock || i.currentStock || 0) > 0).length,
        revenueGrowth: 12.5,
        equipmentUtilization: items.length > 0 ? ((items.filter(i => (i.CurrentStock || i.currentStock || 0) > 0).length / items.length) * 100).toFixed(1) : 0
      });

      setRecentActivities(allActivities);
      setAlerts(newAlerts);

      // Process calendar events from estimates
      const events = estimates
        .filter(e => e.EstimateDate || e.EventDate)
        .map(e => ({
          id: e.Id,
          date: new Date(e.EstimateDate || e.EventDate),
          eventName: e.EventName || e.CustomerName || 'Event',
          address: e.Address || e.EventAddress || 'No address',
          customerName: e.CustomerName || 'Client',
          amount: e.TotalAmount || 0
        }));
      
      setCalendarEvents(events);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set empty data on error so dashboard still shows
      setDashboardData({
        todayRevenue: 0,
        monthRevenue: 0,
        totalSales: 0,
        deliveryOutTotal: 0,
        deliveryOutPending: 0,
        deliveryOutCompleted: 0,
        deliveryInTotal: 0,
        deliveryInPending: 0,
        deliveryInCompleted: 0,
        estimatesTotal: 0,
        estimatesPending: 0,
        estimatesApproved: 0,
        estimatesRejected: 0,
        enquiriesTotal: 0,
        enquiriesPending: 0,
        enquiriesConverted: 0,
        totalClients: 0,
        totalVendors: 0,
        receivables: 0,
        payables: 0,
        cashBalance: 0,
        totalReceipts: 0,
        totalPayments: 0,
        availableEquipment: 0,
        revenueGrowth: 0,
        equipmentUtilization: 0
      });
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  }, [token, companyId, branchId, fromDate, toDate]);

  useEffect(() => {
    console.log('Home component mounted');
    console.log('Token:', token ? 'exists' : 'missing');
    console.log('User:', user);
    
    if (!validateSession()) {
      console.log('Session validation failed');
      setLoading(false);
      return;
    }
    
    console.log('Session validated, fetching data...');
    
    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.log('Loading timeout reached, forcing loading to false');
      setLoading(false);
    }, 15000);
    
    fetchDashboardData();
    
    return () => {
      clearTimeout(loadingTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <OrbitalLoader message="Loading dashboard..." />;
  }

  return (
    <div className="erp-dashboard">
      {/* Summary Cards Row */}
      <div className="summary-cards-row">
        {/* Total Sales */}
        <div className="summary-card" onClick={() => history.push('/SaleIndex')}>
          <div className="card-header">
            <div className="card-icon revenue">
              <TrendingUp />
            </div>
            <div className="card-header-content">
              <span className="card-label">Total Sales</span>
              <div className="card-value">{formatCurrency(dashboardData.totalSales)}</div>
            </div>
          </div>
        </div>

        {/* Delivery Out */}
        <div className="summary-card" onClick={() => history.push('/DeliveryIndex')}>
          <div className="card-header">
            <div className="card-icon stock">
              <LocalShipping />
            </div>
            <div className="card-header-content">
              <span className="card-label">Delivery Out</span>
              <div className="card-value">{formatNumber(dashboardData.deliveryOutTotal)}</div>
            </div>
          </div>
        </div>

        {/* Delivery In */}
        <div className="summary-card" onClick={() => history.push('/DeliveryInIndex')}>
          <div className="card-header">
            <div className="card-icon payable">
              <LocalShipping />
            </div>
            <div className="card-header-content">
              <span className="card-label">Delivery Return</span>
              <div className="card-value">{formatNumber(dashboardData.deliveryInTotal)}</div>
            </div>
          </div>
        </div>

        {/* Estimates */}
        <div className="summary-card" onClick={() => history.push('/EstimateIndex')}>
          <div className="card-header">
            <div className="card-icon receivable">
              <Receipt />
            </div>
            <div className="card-header-content">
              <span className="card-label">Estimates</span>
              <div className="card-value">{formatNumber(dashboardData.estimatesTotal)}</div>
            </div>
          </div>
        </div>

        {/* Enquiries */}
        <div className="summary-card" onClick={() => history.push('/EventItemEnquiryIndex')}>
          <div className="card-header">
            <div className="card-icon cashflow">
              <Assessment />
            </div>
            <div className="card-header-content">
              <span className="card-label">Rental Enquiries</span>
              <div className="card-value">{formatNumber(dashboardData.enquiriesTotal)}</div>
            </div>
          </div>
        </div>

        {/* Product Enquiry */}
        <div className="summary-card" onClick={() => setEnquiryModalOpen(true)}>
          <div className="card-header">
            <div className="card-icon revenue">
              <Assignment />
            </div>
            <div className="card-header-content">
              <span className="card-label">Product Enquiry</span>
              <div className="card-value">New</div>
            </div>
          </div>
        </div>

        {/* Payment & Receipt */}
        <div className="summary-card" onClick={() => history.push('/PaymentReceiptsIndex')}>
          <div className="card-header">
            <div className="card-icon expenses">
              <Payment />
            </div>
            <div className="card-header-content">
              <span className="card-label">Payment & Receipt</span>
              <div className="card-value">{formatCurrency(dashboardData.cashBalance)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="dashboard-main">
        {/* Center Content - Full Width */}
        <div className="center-content-full">
          {/* Four Column Section - Calendar, Expense, Bookings, Branch Revenue */}
          <div className="four-column-section">
            {/* Calendar Section */}
            <div className="chart-card calendar-card-small">
              <div className="calendar-header">
                <h3>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
                <div className="calendar-nav">
                  <button onClick={() => changeMonth(-1)} className="calendar-nav-btn">
                    <span>‹</span>
                  </button>
                  <button onClick={() => changeMonth(1)} className="calendar-nav-btn">
                    <span>›</span>
                  </button>
                </div>
              </div>
              
              <div className="calendar-grid-small">
                <div className="calendar-weekdays-small">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                    <div key={day} className="calendar-weekday-small">{day}</div>
                  ))}
                </div>
                
                <div className="calendar-days-small">
                  {(() => {
                    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
                    const days = [];
                    
                    // Empty cells for days before month starts
                    for (let i = 0; i < startingDayOfWeek; i++) {
                      days.push(<div key={`empty-${i}`} className="calendar-day-small empty"></div>);
                    }
                    
                    // Days of the month
                    for (let day = 1; day <= daysInMonth; day++) {
                      const date = new Date(year, month, day);
                      const events = getEventsForDate(date);
                      const hasEvents = events.length > 0;
                      const isTodayDate = isToday(date);
                      
                      days.push(
                        <div 
                          key={day} 
                          className={`calendar-day-small ${isTodayDate ? 'today' : ''} ${hasEvents ? 'has-event' : ''}`}
                          title={hasEvents ? events.map(e => `${e.eventName} - ${e.address}`).join('\n') : ''}
                          onClick={() => handleDateClick(date)}
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="day-number-small">{day}</span>
                        </div>
                      );
                    }
                    
                    return days;
                  })()}
                </div>
              </div>
              
              {/* Event List Below Calendar */}
              {calendarEvents.filter(e => {
                const eventDate = new Date(e.date);
                return eventDate.getMonth() === currentMonth.getMonth() && 
                       eventDate.getFullYear() === currentMonth.getFullYear();
              }).slice(0, 3).map((event, idx) => (
                <div key={idx} className="calendar-event-item">
                  <div className="event-date-badge">
                    {new Date(event.date).getDate()}
                  </div>
                  <div className="event-details">
                    <div className="event-name">{event.eventName}</div>
                    <div className="event-location">{event.address}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Expense Breakdown - Placeholder */}
            <div className="chart-card expense-card">
              <div className="section-header">
                <h3>Expense Breakdown</h3>
              </div>
              <div className="expense-content">
                <div className="expense-donut">
                  <svg viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#ec4899" strokeWidth="20" strokeDasharray="75 251" transform="rotate(-90 50 50)" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#8b5cf6" strokeWidth="20" strokeDasharray="50 251" strokeDashoffset="-75" transform="rotate(-90 50 50)" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#6366f1" strokeWidth="20" strokeDasharray="63 251" strokeDashoffset="-125" transform="rotate(-90 50 50)" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#3b82f6" strokeWidth="20" strokeDasharray="63 251" strokeDashoffset="-188" transform="rotate(-90 50 50)" />
                  </svg>
                  <div className="donut-center-text">
                    <div className="donut-total">Total</div>
                    <div className="donut-amount">{formatCurrency(dashboardData.totalSales)}</div>
                  </div>
                </div>
                <div className="expense-legend">
                  <div className="legend-item-small">
                    <span className="legend-color" style={{background: '#ec4899'}}></span>
                    <span className="legend-label">Sales</span>
                    <span className="legend-value">{formatCurrency(dashboardData.totalSales)}</span>
                  </div>
                  <div className="legend-item-small">
                    <span className="legend-color" style={{background: '#8b5cf6'}}></span>
                    <span className="legend-label">Delivery</span>
                    <span className="legend-value">{dashboardData.deliveryOutTotal}</span>
                  </div>
                  <div className="legend-item-small">
                    <span className="legend-color" style={{background: '#6366f1'}}></span>
                    <span className="legend-label">Estimates</span>
                    <span className="legend-value">{dashboardData.estimatesTotal}</span>
                  </div>
                  <div className="legend-item-small">
                    <span className="legend-color" style={{background: '#3b82f6'}}></span>
                    <span className="legend-label">Enquiries</span>
                    <span className="legend-value">{dashboardData.enquiriesTotal}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bookings Overview - Placeholder */}
            <div className="chart-card bookings-card">
              <div className="section-header">
                <h3>Bookings Overview</h3>
                <span className="overview-period">This Week</span>
              </div>
              <div className="bookings-content">
                <div className="bookings-stat">
                  <div className="stat-number-large">{dashboardData.estimatesTotal}</div>
                  <div className="stat-label-small">Bookings</div>
                </div>
                <div className="bookings-chart">
                  <svg viewBox="0 0 300 100" preserveAspectRatio="none">
                    <path 
                      d="M 0,80 Q 50,20 100,40 T 200,30 T 300,50" 
                      fill="none" 
                      stroke="url(#gradient)" 
                      strokeWidth="3"
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#ec4899" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="bookings-days">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <span key={day} className="day-label-small">{day}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Branch Wise Revenue - Compact */}
            <div className="chart-card branch-revenue-card-compact">
              <div className="section-header-compact">
                <div className="header-with-count">
                  <h3>Company Revenue</h3>
                  {branchRevenueData.length > 0 && (
                    <span className="branch-count-badge">{branchRevenueData.length}</span>
                  )}
                </div>
                <button className="refresh-btn-compact" onClick={fetchBranchRevenue} disabled={loadingBranchRevenue}>
                  <Refresh className={loadingBranchRevenue ? 'spinning' : ''} />
                </button>
              </div>
              <div className="branch-revenue-content-compact">
                {loadingBranchRevenue ? (
                  <div className="loading-state-compact">
                    <Refresh className="spinning" style={{ fontSize: '24px' }} />
                  </div>
                ) : branchRevenueData.length > 0 ? (
                  <div className="branch-revenue-list">
                    {branchRevenueData
                      .sort((a, b) => (b.TotalRevenue || b.totalRevenue || 0) - (a.TotalRevenue || a.totalRevenue || 0))
                      .map((branch, index) => {
                        const revenue = branch.TotalRevenue || branch.totalRevenue || 0;
                        const isNearLimit = revenue >= 1950000; // 19.5 Lakhs
                        const isOverLimit = revenue >= 2000000; // 20 Lakhs
                        
                        return (
                          <div key={index} className={`branch-item ${isNearLimit ? 'near-limit' : ''} ${isOverLimit ? 'over-limit' : ''}`}>
                            <div className="branch-rank">{index + 1}</div>
                            <div className="branch-info">
                              <div className="branch-header-row">
                                <div className="branch-name-compact">{branch.BranchName || branch.branchName || 'N/A'}</div>
                                {(branch.FinancialYear || branch.financialYear || branch.FinYear || branch.finYear) && (
                                  <span className="finyear-badge">
                                    <span className="finyear-icon">📅</span>
                                    FY {branch.FinancialYear || branch.financialYear || branch.FinYear || branch.finYear}
                                  </span>
                                )}
                              </div>
                              <div className="branch-stats">
                                <div className="revenue-info">
                                  <span className="revenue-label">Revenue</span>
                                  <span className={`revenue-compact ${isNearLimit ? 'revenue-warning' : ''}`}>
                                    {formatCurrency(revenue)}
                                  </span>
                                </div>
                                {isNearLimit && (
                                  <div className="limit-indicator">
                                    <Warning style={{ fontSize: '14px' }} />
                                    <span>{isOverLimit ? 'Over Limit' : 'Near Limit'}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="empty-state-compact">
                    <Info style={{ fontSize: '32px', color: '#d1d5db' }} />
                    <p>No data</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Row - Activities and Alerts */}
          <div className="bottom-cards-row">
            {/* Recent Activities */}
            <div className="chart-card recent-activities-card">
              <div className="activities-header">
                <div className="section-title">
                  <BarChart style={{ color: '#4f46e5' }} />
                  <h3>Recent Activities</h3>
                </div>
                <button className="view-all-link" onClick={() => history.push('/sale')}>
                  View All
                </button>
              </div>
              <div className="activities-list">
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className={`activity-icon ${activity.type}`}>
                        {activity.type === 'sale' && <Receipt />}
                        {activity.type === 'purchase' && <ShoppingCart />}
                        {activity.type === 'payment' && <Payment />}
                        {activity.type === 'delivery' && <LocalShipping />}
                        {activity.type === 'estimate' && <Assessment />}
                      </div>
                      <div className="activity-info">
                        <span className="activity-title">{activity.title}</span>
                        <span className="activity-amount">{formatCurrency(activity.amount)}</span>
                      </div>
                      <span className="activity-date">{activity.date}</span>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <Info style={{ fontSize: '48px', color: '#d1d5db' }} />
                    <p>No recent activities</p>
                  </div>
                )}
              </div>
            </div>

            {/* Alerts & Notifications */}
            <div className="chart-card alerts-section">
              <div className="section-header">
                <div className="section-title">
                  <Warning style={{ color: '#f59e0b' }} />
                  <h3>Important Alerts</h3>
                </div>
                <span className="alert-count">{alerts.length}</span>
              </div>
              <div className="alerts-list">
                {alerts.length > 0 ? (
                  alerts.map((alert, index) => (
                    <div key={index} className={`alert-item ${alert.type}`}>
                      <div className="alert-icon">
                        {alert.type === 'overdue' && <Error />}
                        {alert.type === 'pending' && <Schedule />}
                        {alert.type === 'stock' && <Inventory />}
                      </div>
                      <div className="alert-content">
                        <span className="alert-title">{alert.title}</span>
                        {alert.amount && (
                          <span className="alert-amount">
                            {formatCurrency(alert.amount)}
                          </span>
                        )}
                        <span className="alert-note">{alert.note}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <CheckCircle style={{ fontSize: '48px', color: '#10b981' }} />
                    <p>All clear! No alerts</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Enquiry Modal */}
      <EnquiryModal
        open={enquiryModalOpen}
        onClose={() => setEnquiryModalOpen(false)}
        onSave={() => {
          setEnquiryModalOpen(false);
          fetchDashboardData();
        }}
        enquiryData={null}
        mode="create"
        customers={customers}
        items={items}
      />

      {/* Date Report Modal */}
      {reportModalOpen && (
        <div className="modal-overlay" onClick={() => setReportModalOpen(false)}>
          <div className="modal-content report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Forecast Report - {formatDateDDMMYYYY(selectedDateData?.date)}</h2>
              <button className="modal-close" onClick={() => setReportModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              {loadingReport ? (
                <div className="loading-state">
                  <Refresh className="spinning" />
                  <p>Loading report data...</p>
                </div>
              ) : selectedDateData?.error ? (
                <div className="error-state">
                  <Error style={{ fontSize: '48px', color: '#ef4444' }} />
                  <p>{selectedDateData.error}</p>
                </div>
              ) : selectedDateData?.data && selectedDateData.data.length > 0 ? (
                <div className="report-data-grouped">
                  {groupDataByCustomer(selectedDateData.data).map((customer, index) => {
                    const customerKey = `${customer.customerName}_${customer.contactNumber}_${customer.addressLine1}`;
                    const isExpanded = expandedCustomers[customerKey];
                    
                    return (
                      <div key={index} className="customer-group">
                        {/* Customer Header */}
                        <div 
                          className="customer-header"
                          onClick={() => toggleCustomer(customerKey)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="customer-info">
                            <div className="customer-name-row">
                              <span className="expand-icon">
                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </span>
                              <div className="customer-details">
                                <div className="customer-name">{customer.customerName || 'N/A'}</div>
                                <div className="customer-contact">{customer.contactNumber || 'N/A'}</div>
                              </div>
                            </div>
                            <div className="customer-address">
                              {customer.addressLine1 && <div>{customer.addressLine1}</div>}
                              {customer.addressLine2 && <div className="address-line2">{customer.addressLine2}</div>}
                            </div>
                            <div className="items-count-badge">
                              {customer.items.length} {customer.items.length === 1 ? 'Item' : 'Items'}
                            </div>
                          </div>
                        </div>

                        {/* Items Table - Collapsible */}
                        {isExpanded && (
                          <div className="items-table-container">
                            <table className="items-table">
                              <thead>
                                <tr>
                                  <th>Item Name</th>
                                  <th>Forecast Qty</th>
                                  <th>Delivery Date</th>
                                  <th>Return Date</th>
                                  <th>Expected Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                {customer.items.map((item, itemIndex) => (
                                  <tr key={itemIndex}>
                                    <td className="item-name">{item.Name || 'N/A'}</td>
                                    <td className="qty-cell">{item.ForecastQty || 0}</td>
                                    <td>{formatDateDDMMYYYY(item.DeliveryDate)}</td>
                                    <td>{formatDateDDMMYYYY(item.DeliveryReturnDate)}</td>
                                    <td className="time-cell">{item.ExpectedTime || 'N/A'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <Info style={{ fontSize: '48px', color: '#d1d5db' }} />
                  <p>No data available for this date</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;